import React, { useState } from 'react'
import LoginCodeDialog from './login-code-dialog'
import ForgotPasswordDialog from './forgot-password-dialog'
import { login, trySSOLogin } from '../../services/login'
import ErrorDialog from './error-dialog'
import LoginPageWeb from './login-page-web'
import LoginPageTablet from './login-page-tablet'

const LoginPage = (props) => {
  const [ email, setEmail ] = useState(null)
  const [ password, setPassword ] = useState(null)
  const [ inputError, setInputError ] = useState(null)
  const [ dialogError, setDialogError ] = useState(null)

  const [ codeDialogOpen, setCodeDialogOpen ] = useState(false)
  const [ passwordDialogOpen, setPasswordDialogOpen ] = useState(false)
  const [ passwordResetState, setPasswordResetState ] = useState(0) // 0 = none, 1 = pending, 2 = sent, -1 = fail

  const [ loading, setLoading ] = useState(false)
  const [ usePasswordLogin, setUserPasswordLogin ] = useState(false)

  const web = props.web ?? false

  const onLoginFailed = (error) => {
    setLoading(false)
    switch (error.code) {
      case 'invalid_request':
        // Invalid request body (required fields missing)
        // OR unknown realm non-existent-connection
        setInputError('Both fields are required')
        break
      case 'unauthorized_client':
        // Cross origin login not allowed.
        setDialogError({ header: 'Login error', body: <p>We&apos;re really sorry, but it seems the impossible has occurred.<br />Please try again and if this problem persists, contact support with error code 'broken arrow'.</p> })
        break
      case 'unsupported_credential_type':
        // Unknown credential type parameter.
        setDialogError({ header: 'Login error', body: <p>We&apos;re really sorry, but it seems the impossible has occurred.<br />Please try again and if this problem persists, contact support with error code 'unsupported credential'.</p> })
        break
      case 'access_denied':
        // Could also be an authentication error, but we don't know that unfortunately
        setInputError('Wrong email or password')
        break
      case 'blocked-user':
        // Blocked user
        setDialogError({ header: 'Blocked user', body: <p>This email address has been blocked from logging into Glue.<br />Please contact support.</p> })
        break
      case 'password-leaked':
        setDialogError({ header: 'Password leaked', body: <p>This login attempt has been blocked because the password you&apos;re using<br />was previously disclosed through a data breach (not in this application).</p> })
        break
      case 'too-many-attempts':
        setDialogError({ header: 'Too many login attempts', body: <p>Your account has been blocked after multiple consecutive login attempts.<br /> We&apos;ve sent you an email with instructions on how to unblock it.</p> })
        break
      default:
        setDialogError({ header: 'Unknown error', body: <p>We have to be honest, we don't know what went wrong this time.<br />Please try again and if that doesn't resolve the problem, contact Glue support.</p> })
        break
    }
  }

  // Try to use SSO login, if that doesn't work, enable password field
  const onPressNextButton = async () => {
    if (loading) {
      return
    }

    if (!email) {
      setInputError('Required field')
      return
    }

    setLoading(true)
    if (!await trySSOLogin(email)) {
      setUserPasswordLogin(true)
      setLoading(false)
    }
  }

  const onPressLoginButton = () => {
    if (loading) {
      return
    }

    setLoading(true)
    login(email, password, onLoginFailed)
  }

  const onCodeDialogClose = (idToken) => {
    setCodeDialogOpen(false)

    if (!idToken) {
      return
    }

    // Log in and refresh page
    window.localStorage.setItem('idToken', idToken)
    window.location = window.location.protocol + '//' + window.location.host
  }

  const onPasswordResetDialogOpen = () => {
    setPasswordResetState(0)
    setPasswordDialogOpen(true)
  }

  const onPasswordResetDialogClose = () => {
    setPasswordDialogOpen(false)
  }

  return (
    <>
      {web ? (
        <LoginPageWeb
          loading={loading}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          inputError={inputError}
          setInputError={setInputError}
          usePasswordLogin={usePasswordLogin}
          onPressNextButton={onPressNextButton}
          onPressLoginButton={onPressLoginButton}
          openPasswordDialog={onPasswordResetDialogOpen}
        />
      ) : (
        <LoginPageTablet
          loading={loading}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          inputError={inputError}
          setInputError={setInputError}
          usePasswordLogin={usePasswordLogin}
          onPressNextButton={onPressNextButton}
          onPressLoginButton={onPressLoginButton}
          openPasswordDialog={onPasswordResetDialogOpen}
          setCodeDialogOpen={setCodeDialogOpen}
        />
      )}
      <LoginCodeDialog open={codeDialogOpen} onCloseDialog={onCodeDialogClose} />
      <ForgotPasswordDialog web={web} open={passwordDialogOpen} email={email} state={passwordResetState} onChangeState={setPasswordResetState} onCloseDialog={onPasswordResetDialogClose} />
      <ErrorDialog open={!!dialogError} header={dialogError?.header} body={dialogError?.body} onCloseDialog={() => setDialogError(null)} />
    </>
  )
}

export default LoginPage
